import './index.css'

import get from 'lodash/get'
import * as React from 'react'
import { connect } from 'react-redux'
import InboxesModalSvg from '!@svgr/webpack!./assets/images/inboxes-modal.svg'

import mixpanel from '@disco/api/mixpanel'
import { CUSTOM_INBOXES } from '@disco/constants/FeatureTypes'
import {
  CUSTOM_INBOX,
  PERSONAL_INBOX,
  TEAM_INBOX,
} from '@disco/constants/GroupTypes'
import * as ExplorerActions from '@disco/state-management/actions/ExplorerActions'
import * as GroupsActions from '@disco/state-management/actions/GroupActions'
import { isDeviceTypePhone } from '@disco/state-management/selectors/app'
import { makeGetFeatureData } from '@disco/state-management/selectors/business'

import type { Group } from 'types/group'
import type { State } from 'types/redux'
import type { User } from 'types/user'

import ClipboardInput from 'components/common/clipboard-input'
import InboxList from 'components/common/custom-inboxes/inbox-list'
import GroupModal from 'components/common/custom-libraries/edit-form'
import Row from 'components/common/row'
import HelpTooltip from 'components/common/tooltip'
import DocumentHeadTags from 'components/document-head-tags'
import Alert from 'components/ui/alert'
import Button from 'components/ui/button'
import ContactUsLink from 'components/ui/contact-us-link'
import DiscoModal from 'components/ui/disco-modal'
import InlineLink from 'components/ui/inline-link'
import PageContainer from 'components/ui/page-container'
import PageHeader from 'components/ui/page-header'
import PricingBadge from 'components/ui/pricing-badge'

function mapStateToProps(state: State) {
  const getFeatureData = makeGetFeatureData()
  const { isTrial, isFeatureEnabled } = getFeatureData(state, {
    featureName: CUSTOM_INBOXES,
  })
  return {
    ...state.groups,
    user: state.user,
    isTrial,
    isFeatureEnabled,
    isPhone: isDeviceTypePhone(state),
  }
}

const mapDispatchToProps = { ...GroupsActions, ...ExplorerActions }
type Props = {
  user: User
  groups: Group[]
  isTrial: boolean
  isFeatureEnabled: boolean
  isPhone: boolean
} & typeof mapDispatchToProps

class InboxesSettings extends React.Component<
  Props,
  {
    hideAutoPlaylists: boolean
    showNewInboxModal: boolean
    showUpgradeModal: boolean
  }
> {
  constructor(props: Props) {
    super(props)
    this.state = {
      hideAutoPlaylists: get(
        this.props.user,
        'settings.hide_auto_playlists',
        false,
      ),
      showNewInboxModal: false,
      showUpgradeModal: false,
    }
  }

  componentDidMount() {
    mixpanel.sendPureEvent('Open Inboxes tab in settings')

    this._load()
  }

  async _load() {
    await this.props.loadGroups()
  }

  _handleAutoPlaylistsChange = () => {
    this.setState({
      hideAutoPlaylists: !this.state.hideAutoPlaylists,
    })
  }
  _newGroup = () => {
    const { isFeatureEnabled } = this.props

    if (!isFeatureEnabled) {
      this.setState({
        showUpgradeModal: true,
      })
    } else {
      this.setState({
        showNewInboxModal: true,
      })
    }
  }
  _onCloseModal = () => {
    this.setState({
      showNewInboxModal: false,
      showUpgradeModal: false,
    })
  }
  _onInboxCreate = (_, group) => {
    // Event #8 from https://disco-team.atlassian.net/browse/DISCO-4710
    mixpanel.sendPureEvent('Create custom Inbox', {
      modal: 'Settings',
      type: group.group_type,
      name: group.name,
    })

    this._onCloseModal()
  }

  render() {
    const inboxGroup = this.props.user.groups.filter(group => {
      return group.group_type === PERSONAL_INBOX
    })
    let newEditForm = null

    if (this.state.showNewInboxModal) {
      newEditForm = (
        <GroupModal
          {...this.props}
          groupType={CUSTOM_INBOX}
          onSubmit={this._onInboxCreate}
          onClose={this._onCloseModal}
        />
      )
    }

    let upgradeModal = null

    if (this.state.showUpgradeModal) {
      upgradeModal = (
        <DiscoModal
          onRequestClose={this._onCloseModal}
          style={{
            overlay: {
              zIndex: 1000,
              backgroundColor: '#02120cb3',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            },
          }}
          className="CustomInboxes_Modal flc"
          isOpen={this.state.showUpgradeModal}
        >
          <>
            <h2 className="CustomInboxes_ModalTitle">Cutom inboxes</h2>

            <InboxesModalSvg width={232} height={189} />
            <p className="CustomInboxes_ModalDescription">
              Want more flexibility with your inboxes? With custom inboxes, you
              can create inboxes for different projects or senders and keep
              everything organized.
            </p>

            <div className="CustomInboxes_ModalButtonWrapper">
              <Button caption="Cancel" onClick={this._onCloseModal} />
              <ContactUsLink
                type="button"
                onClick={this._onCloseModal}
                intercomMsg="I’d like to discuss upgrading to Pro to access Custom Inboxes"
              >
                Upgrade
              </ContactUsLink>
            </div>
          </>
        </DiscoModal>
      )
    }

    const { isFeatureEnabled, isTrial, isPhone } = this.props
    return (
      <>
        <DocumentHeadTags title="Settings | Inboxes | DISCO" />
        <PageHeader
          title={
            <>
              Default inboxes
              <HelpTooltip id="tooltip_settings_inboxes" />
            </>
          }
        />
        <PageContainer>
          {inboxGroup[0] && (
            <Row columnsSize={[12]} marginBottom="small">
              <div>
                <div className="InboxList_Container">
                  <div className="InboxList_Content">
                    <ClipboardInput
                      readOnly
                      label="My Inbox URL"
                      value={inboxGroup[0].upload_url}
                    />
                  </div>
                  <div className="InboxList_ActionButtons" />
                </div>
              </div>
            </Row>
          )}
          <InboxList
            groups={this.props.groups}
            inboxType={TEAM_INBOX}
            {...this.props}
          />
        </PageContainer>
        <PageHeader
          title="Custom inboxes"
          actions={
            <>
              <Button
                appearance="icon"
                icon="add-circle"
                tooltip={!isPhone ? 'New inbox' : undefined}
                onClick={this._newGroup}
              />
              {!isFeatureEnabled && <PricingBadge title="PRO" />}
            </>
          }
        />
        <PageContainer>
          <InboxList
            groups={this.props.groups}
            inboxType={CUSTOM_INBOX}
            {...this.props}
          />
          {newEditForm}
          {upgradeModal}
          {(isTrial || !isFeatureEnabled) && (
            <Alert icon="warning" status="warning">
              Want more flexibility with your inboxes? With custom inboxes, you
              can create inboxes for different projects or senders and keep
              everything organised. Custom inbox are available on&nbsp;
              <InlineLink to="/settings/billing">PRO</InlineLink>
            </Alert>
          )}
        </PageContainer>
      </>
    )
  }
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(InboxesSettings) as any
